import React, { useEffect, useState, useContext } from 'react';
import {
    Box,
    Typography,
    TablePagination,
    TextField,
    Button,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TableComponent from '../../widgets/TableComponent';
import RequestService from '../../services/RequestService';
import { UserContext } from '../../contexts/UserContext';
import RewardBadge from '../../widgets/RewardBadge';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';

const QuizHistoryPage = () => {
    const { user } = useContext(UserContext);

    const [rows, setRows] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [userId, setUserId] = useState('');
    const [quizId, setQuizId] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [users, setUsers] = useState([]);
    const [quizzes, setQuizzes] = useState([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fromArrayDate = (arr) => {
        if (!Array.isArray(arr) || arr.length < 6) return null;
        const [y, m, d, hh, mm, ss, ns] = arr;
        const millis = ns != null ? Math.floor(ns / 1_000_000) : 0;
        return new Date(
            y,
            (m ?? 1) - 1,
            d ?? 1,
            hh ?? 0,
            mm ?? 0,
            ss ?? 0,
            millis
        );
    };

    const fetchHistory = async () => {
        if (!user?.company?.id) return;
        try {
            const data = await RequestService.getQuizHistoryWithFilters(
                user.company.id,
                {
                    page,
                    size: rowsPerPage,
                    userId: userId || undefined,
                    quizId: quizId || undefined,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                }
            );

            const items = Array.isArray(data?.items) ? data.items : [];
            setRows(items);
            setTotalElements(
                Number.isFinite(data?.totalElements)
                    ? data.totalElements
                    : items.length
            );
        } catch (err) {
            console.error('Erro ao carregar histórico de quizzes:', err);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [page, rowsPerPage]);

    useEffect(() => {
        const fetchFilters = async () => {
            if (!user || !user.company || !user.company.id) return;
            try {
                const userData = await RequestService.getUsersByCompany(
                    user.company.id
                );
                setUsers(
                    Array.isArray(userData)
                        ? userData
                        : (userData.items ?? userData.content ?? [])
                );

                const quizData = await RequestService.getQuizzes(
                    user.company.id
                );

                setQuizzes(Array.isArray(quizData) ? quizData : []);
            } catch (err) {
                console.error('Erro ao carregar filtros:', err);
            }
        };
        fetchFilters();
    }, [user?.company?.id]);

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const columns = [
        {
            label: 'Quiz',
            field: 'quizTitle',
            renderCell: (_, r) => r?.quizTitle ?? '-',
        },
        {
            label: 'Usuário',
            field: 'userName',
            renderCell: (_, r) => r?.userName ?? '-',
        },
        {
            label: 'Acertos',
            field: 'correctCount',
            renderCell: (_, r) =>
                r?.totalQuestions != null && r?.correctCount != null
                    ? `${r.correctCount}`
                    : '-',
        },
        {
            label: 'Erros',
            field: 'wrongCount',
            renderCell: (_, r) => r?.wrongCount ?? '-',
        },
        {
            label: 'Moedas',
            field: 'coinsEarned',
            // renderCell: (_, r) => r?.coinsEarned ?? 0,
            renderCell: (_, r) => (
                <RewardBadge
                    icon={
                        <CoinIcon
                            style={{ color: '#134F99', fontSize: '24px' }}
                        />
                    }
                    value={r?.coinsEarned || 0}
                />
            ),
        },
        {
            label: 'Concluído em',
            field: 'completedAt',
            renderCell: (_, r) => {
                const dt = fromArrayDate(r?.completedAt);
                return dt ? dt.toLocaleString() : '-';
            },
        },
        {
            label: 'Aproveitamento',
            field: 'accuracy',
            renderCell: (_, r) => {
                if (!r?.totalQuestions) return '-';
                const pct = (r.correctCount / r.totalQuestions) * 100;
                return `${pct.toFixed(0)}%`;
            },
        },
    ];

    const renderMobile = () => {
        if (rows.length === 0)
            return <Typography>Nenhum registro encontrado.</Typography>;
        return (
            <Box>
                {rows.map((item) => {
                    const dt = fromArrayDate(item?.completedAt);
                    return (
                        <Box
                            key={item.attemptId}
                            sx={{
                                border: '1px solid #ddd',
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                            }}
                        >
                            <Typography variant="body2">
                                Quiz: {item?.quizTitle ?? '-'}
                            </Typography>
                            <Typography variant="body2">
                                Usuário: {item?.userName ?? '-'}
                            </Typography>
                            <Typography variant="body2">
                                Acertos: {item?.correctCount ?? 0}/
                                {item?.totalQuestions ?? 0}
                            </Typography>
                            <Typography variant="body2">
                                Erros: {item?.wrongCount ?? 0}
                            </Typography>
                            <Typography variant="body2">
                                Moedas: {item?.coinsEarned ?? 0}
                            </Typography>
                            <Typography variant="body2">
                                Concluído em: {dt ? dt.toLocaleString() : '-'}
                            </Typography>
                        </Box>
                    );
                })}
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 20]}
                />
            </Box>
        );
    };

    const renderDesktop = () => (
        <Box>
            <TableComponent columns={columns} rows={rows} />
            <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20, 50]}
            />
        </Box>
    );

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        GameUP
                    </Link>
                    <Link underline="hover" color="inherit">
                        Quizzes
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Histórico
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: 500,
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Histórico de Quizzes
                </Typography>
            </Grid>

            <Box sx={{ my: 2 }}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Usuário</InputLabel>
                        <Select
                            value={userId}
                            label="Usuário"
                            onChange={(e) => setUserId(String(e.target.value))}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {users.map((u) => (
                                <MenuItem key={u.id} value={String(u.id)}>
                                    {u.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 220 }} size="small">
                        <InputLabel>Quiz</InputLabel>
                        <Select
                            value={quizId}
                            label="Quiz"
                            onChange={(e) => setQuizId(String(e.target.value))}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {quizzes.map((q) => (
                                <MenuItem key={q.id} value={q.id}>
                                    {q.title ?? q.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        sx={{ minWidth: 180 }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        sx={{ minWidth: 180 }}
                        InputLabelProps={{ shrink: true }}
                    />

                    <Button
                        variant="contained"
                        sx={{ height: 40 }}
                        onClick={() => {
                            setPage(0);
                            fetchHistory();
                        }}
                    >
                        Aplicar Filtros
                    </Button>
                </Box>
            </Box>

            {isMobile ? renderMobile() : renderDesktop()}
        </Box>
    );
};

export default QuizHistoryPage;
